import { createFileRoute, lazyRouteComponent } from "@tanstack/react-router";

import type {
  PortalBuildingsSortBy,
  PortalBuildingsSortDir,
} from "@/api/portal";
// PR-9 — the tab half of this route's search contract. IMPORTED, never
// re-typed here: the page's idea of a legal tab and the URL's idea of a
// legal tab have to be ONE idea, or a tab legal in one half and illegal in
// the other renders a blank surface off a URL the router happily kept. That
// is the improvement PR-4 made on the shipped LL97 pair (which spells its
// list twice), and this route keeps it. The full contract — including what
// an unknown `?tab=` does and why the default is applied on the render half
// only — is stated once, in `pages/Portal/Dashboard/tabs.ts`.
import {
  validateDashboardTabSearch,
  type DashboardTabRouteSearch,
} from "@/pages/Portal/Dashboard/tabs";

/**
 * feat/portal-enforcement-split — the Buildings table is server-sorted, and
 * the active sort lives in the URL so it survives a reload / back-forward
 * and is shareable. Only ``sortBy`` / ``sortDir`` are URL-driven; ``page``
 * and the search term stay component-local (transient, per-session).
 *
 * Defaults mirror the BE contract exactly (``enforcement`` / ``desc``) so
 * a bare ``/portal`` URL keys the same query slot the WelcomeWarmup prefetch
 * warmed — no re-sort, no empty flash on first paint.
 *
 * The fields are typed OPTIONAL so the router does NOT force a ``search``
 * prop onto the dozens of existing ``<Link to="/portal">`` / ``navigate({to:
 * "/portal"})`` call sites across the app. ``validateSearch`` still fills
 * concrete defaults at runtime, so every read gets a defined value.
 *
 * PR-9 adds ``tab``, and it behaves the OPPOSITE way to the two above: an
 * unrecognised value becomes ``undefined`` rather than a default. That is
 * deliberate and the two rules are not inconsistent. ``sortBy``/``sortDir``
 * default here because the bare URL must key the prefetched query slot;
 * ``tab`` must not, because defaulting it here would stamp
 * ``?tab=buildings`` onto every shipped ``<Link to="/portal">`` and the
 * clean ``/portal`` URL would stop existing. Each half carries its reason.
 */
export type PortalDashboardSearch = {
  sortBy?: PortalBuildingsSortBy;
  sortDir?: PortalBuildingsSortDir;
} & DashboardTabRouteSearch;

/**
 * Exported separately from {@link Route} so unit tests can pin the parser
 * contract (unknown values coerce to the default, always a valid shape)
 * without spinning up a full router.
 */
export const validatePortalDashboardSearch = (
  search?: Record<string, unknown>,
): PortalDashboardSearch => {
  const sb = search?.sortBy;
  const sd = search?.sortDir;
  return {
    // ABSENT STAYS ABSENT — the same rule `tab` follows three lines down, and
    // now for the same reason: a bare `/portal` must stay a bare `/portal`.
    //
    // THIS USED TO FILL CONCRETE DEFAULTS, and the docblock above justified it
    // on the grounds that "the bare URL must key the query slot the
    // WelcomeWarmup prefetch warmed". That reason is stale. The query layer
    // normalises on the way in — `portalQueries.ts` keys on
    // `params.sortBy ?? "enforcement"` / `params.sortDir ?? "desc"` — so an
    // absent pair and an explicit `enforcement`/`desc` pair produce the SAME
    // cache key and the prefetch still hits. `Page.tsx` defaults the same way
    // before it reads them.
    //
    // Filling them here cost more than the clean URL: it also made
    // "did the reader ask for this order?" unanswerable, because
    // `rawSearch.sortBy` was never undefined. A guard built on that question
    // silently never fired, and the sort-persistence feature it gated spent a
    // release overwriting the reader's remembered order with the default.
    //
    // `risk` is NOT admitted any more. The tier grouping is drawn on every
    // one-page portfolio and is not an order a reader picks, so a stale
    // bookmark carrying it is corrected out of the URL by the dashboard rather
    // than round-tripped as a sort the table cannot draw.
    ...(sb === "projection" ? { sortBy: sb as PortalBuildingsSortBy } : {}),
    ...(sd === "asc" ? { sortDir: sd as PortalBuildingsSortDir } : {}),
    // Spread, not a hand-written `tab:` line — the guard is the tab list's
    // single reader, and an unknown tab must come back ABSENT (the key is
    // present with `undefined`, which TanStack drops from the URL) rather
    // than round-tripped in a shape the page cannot render.
    ...validateDashboardTabSearch(search),
  };
};

// Wave-17 Item 1.1 — lazy-load the Portal dashboard page so the cold-load
// main chunk only pays for the route tree + the routes the user actually
// lands on. lazyRouteComponent integrates with TanStack Router's preload
// + pending UI; Rollup auto-splits the dynamic import into its own chunk.
export const Route = createFileRoute("/portal/")({
  component: lazyRouteComponent(() => import("@/pages/Portal/Dashboard/Page")),
  validateSearch: validatePortalDashboardSearch,
});
